import { useCallback, useState } from 'react'
import { Flex, Layout, Tag, Typography } from 'antd'
import dayjs from 'dayjs'
import { useTranslation } from 'react-i18next'
import { NoteIcon } from 'src/assets/icons'
import { useSelector } from 'react-redux'
import { useUserSetting } from 'src/app/shared/hooks/useUserSetting'
import { TABLE_NAME } from 'src/app/shared/constants/tableName.constants'
import { getBatchProcessing } from 'src/app/shared/services/BackgroundService'
import NewCommonTable from 'src/app/shared/components/common/new-table'
import { TableColumnDefs } from 'src/app/shared/components/common/new-table/table.model'
import { useDatePicker } from 'src/app/shared/hooks/useDatePicker'
import { outerTableRef } from 'src/app/shared/components/common/new-table/components/TableProvider'

import SearchBar from './SearchBar'
import './index.scss'
import { getBatchProcessingsDownloadPdf, getBatchProcessingsDownloadStatus } from 'src/app/shared/services/BusinessAdminLogsService'

function BatchProcessing() {
  const retentionSettings = useSelector((state: any) => state?.masterSetup?.deferredConfig?.retentionSettings)
  const { batchProcessingLog: batchProcessingLogDays } = retentionSettings
  const { isGettingSetting, isUpdatingSetting } = useUserSetting()
  const [isLoading, setIsLoading] = useState(false)
  const [rowData, setRowData] = useState([])
  const [total, setTotal] = useState(0)
  const { getCurrentDate } = useDatePicker()

  const { t } = useTranslation()
  const [dateRange, setDateRange] = useState<any>([getCurrentDate(), getCurrentDate()])

  const generateCustomColumnDefs = useCallback(
    (columnDefs: TableColumnDefs[]): Array<TableColumnDefs> =>
      columnDefs.map(
        (item): TableColumnDefs => ({
          ...item,
          dataType: item?.colId === 'createdAt' ? 'date' : 'text',
        }),
      ),
    [],
  )

  const getBatchProcessingLogs = useCallback(
    async (payload: any) => {
      try {
        setIsLoading(true)
        const dateFrom = dayjs(dateRange?.[0])?.format('YYYY-MM-DD')
        const dateTo = dayjs(dateRange?.[1])?.format('YYYY-MM-DD')
        const response = await getBatchProcessing({ ...payload, dateFrom, dateTo })
        const formattedData = response?.data?.items?.map((item: any) => ({
          ...item,
          createdAt: dayjs(item?.createdAt)?.format('DD/MM/YYYY HH:mm A'),
        }))
        setRowData(formattedData)
        setTotal(response?.data?.total)
        return formattedData
      } catch (error) {
      } finally {
        setIsLoading(false)
      }
    },
    [dateRange],
  )

  const handleSearch = useCallback(
    async (value: Partial<any>) => {
      await getBatchProcessingLogs(value)
    },
    [getBatchProcessingLogs],
  )

  const handleDownload = useCallback(
    async (payload: any) => {
      const dateFrom = dayjs(dateRange?.[0])?.format('YYYY-MM-DD')
      const dateTo = dayjs(dateRange?.[1])?.format('YYYY-MM-DD')

      return await getBatchProcessing({ ...payload, dateFrom, dateTo })
    },
    [dateRange],
  )
  const handleSearchBar = async () => {
    await outerTableRef.current?.handleSearchLogic?.({})
  }

  const handleReset = () => {
    setDateRange([dayjs(new Date()), dayjs(new Date())])
  }

  return (
    <Flex vertical gap={24} className='batch-processing-container'>
      <SearchBar
        dateRange={dateRange}
        setDateRange={setDateRange}
        handleSearch={handleSearchBar}
        handleReset={handleReset}
      />

      <Tag className='note-text-container' icon={<NoteIcon style={{ fontSize: '18px' }} />} color='blue'>
        <Typography.Text className='note-text'>
          {t('BusinessAdminLogs:batchProcessing.note', { days: batchProcessingLogDays })}
        </Typography.Text>
      </Tag>

      <div style={{ height: 400 }}>
        <NewCommonTable
          ref={outerTableRef}
          rowData={rowData}
          total={total}
          displayAddBtn={false}
          displayUploadButton={false}
          displayDownloadBtn
          loading={isLoading}
          displayActionColumn={false}
          pageName={TABLE_NAME.BATCH_PROCESSING}
          tableSettingName={TABLE_NAME.BATCH_PROCESSING}
          isGettingSetting={isGettingSetting}
          isUpdatingSetting={isUpdatingSetting}
          translationNamespace='BusinessAdminLogs:batchProcessing.tableHeader'
          handleSearch={handleSearch}
          handleDownload={handleDownload}
          getDownloadStatus={getBatchProcessingsDownloadStatus}
          getDownloadFile={getBatchProcessingsDownloadPdf}
          generateCustomColumnDefs={generateCustomColumnDefs}
        />
      </div>
    </Flex>
  )
}

export default BatchProcessing
